import React, { useState } from 'react'
import { policzCene } from '../lib/cenyZOfert'

/*
 * Ceny z prawdziwych ofert (artykuł „Planujesz fotowoltaikę w 2027 roku?”).
 * Źródło: VV/dane_badawcze/kalibracja_widelek_2026-10-29/ceny_do_artykulu.py (wynik w ceny_do_artykulu.json): 156 ofert
 * nowych instalacji od 84 firm: ogłoszenia z OLX (7 i 29.10.2026), oferty z dwóch grup na Facebooku (wrzesień
 * i 7.10.2026), cenniki i kalkulator na stronach firm (29.10.2026). Ceny brutto, netto razy 1,08. Słupek to mediana
 * zaokrąglona do 100 zł, kreska to środkowa połowa ofert (kwartyle). Liczby muszą zgadzać się z tabelą w artykule.
 */
const GRUPY = [
  {
    nazwa: 'ok. 5 kWp',
    pv: { mediana: 15000, q1: 13900, q3: 19700, min: 12000, max: 23490, ofert: 8, zlKwp: 3240, opis: '3,6 do 6,3 kWp, mało ofert' },
    mag: { mediana: 33000, q1: 28800, q3: 37300, min: 19000, max: 51000, ofert: 32, zlKwp: 5990, opis: 'magazyn zwykle ok. 12 kWh' },
  },
  {
    nazwa: 'ok. 8 kWp',
    pv: { mediana: 23000, q1: 13000, q3: 27200, min: 12000, max: 35800, ofert: 8, zlKwp: 3200, opis: '6,7 do 8,3 kWp, mało ofert i duży rozrzut' },
    mag: { mediana: 44000, q1: 39900, q3: 48000, min: 26000, max: 73900, ofert: 21, zlKwp: 5650, opis: 'magazyn zwykle ok. 15 kWh' },
  },
  {
    nazwa: 'ok. 10 kWp',
    pv: { mediana: 21500, q1: 20000, q3: 24900, min: 14500, max: 38900, ofert: 20, zlKwp: 2190, opis: 'głównie ogłoszenia z OLX' },
    mag: { mediana: 45000, q1: 39500, q3: 49900, min: 33000, max: 69120, ofert: 47, zlKwp: 4430, opis: 'magazyn zwykle ok. 20 kWh' },
  },
  {
    nazwa: '12 do 20 kWp',
    pv: { mediana: 44700, q1: 34000, q3: 52600, min: 20000, max: 61800, ofert: 5, zlKwp: 3120, opis: 'mało ofert' },
    mag: { mediana: 53900, q1: 49900, q3: 62900, min: 45000, max: 81300, ofert: 14, zlKwp: 4000, opis: 'magazyn zwykle ok. 28 kWh' },
  },
]

// Barwy platformy (błękit i niebieski jak na stronie głównej), sprawdzone walidatorem palety pod kątem daltonizmu:
// jasny motyw sky-400 i blue-700 na białej karcie, ciemny sky-500 i blue-600 na karcie #1c2534.
const SERIE = [
  { klucz: 'pv', nazwa: 'Bez magazynu', kolor: 'bg-sky-400 dark:bg-sky-500', aktywny: 'bg-sky-300 dark:bg-sky-400' },
  { klucz: 'mag', nazwa: 'Z magazynem', kolor: 'bg-blue-700 dark:bg-blue-600', aktywny: 'bg-blue-600 dark:bg-blue-500' },
]

const SKALA = 70000
const PODZIALKA = [0, 20000, 40000, 60000]
const proc = (zl) => `${(zl / SKALA) * 100}%`
// Spacja tysięcy także przy 4 cyfrach („4 290”), jak w tabelach artykułu; `toLocaleString('pl-PL')` jej tam nie stawia.
const liczba = (n) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0')
const zl = (n) => `${liczba(n)} zł`
const tys = (n) => (Math.round(n / 100) / 10).toLocaleString('pl-PL', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
const ofert = (n) => {
  if (n === 1) return '1 oferta'
  const r10 = n % 10
  const r100 = n % 100
  return `${n} ${r10 >= 2 && r10 <= 4 && (r100 < 12 || r100 > 14) ? 'oferty' : 'ofert'}`
}

const Slupek = ({ seria, dane, grupa, aktywny, onAktywny }) => {
  if (!dane) {
    return (
      <div className="relative h-full w-7 flex-none flex items-end justify-center" aria-hidden="true">
        <span className="mb-1 text-[10px] leading-tight text-gray-400 text-center">brak<br />ofert</span>
      </div>
    )
  }
  const opis = `${seria.nazwa}, ${grupa}: mediana ${zl(dane.mediana)}, połowa ofert od ${zl(dane.q1)} do ${zl(dane.q3)}, ${ofert(dane.ofert)}`
  return (
    <button
      type="button"
      className="relative h-full w-7 flex-none cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 rounded"
      onMouseEnter={onAktywny}
      onFocus={onAktywny}
      onClick={onAktywny}
      aria-label={opis}
    >
      <span
        className={`absolute bottom-0 left-0.5 right-0.5 rounded-t ${aktywny ? seria.aktywny : seria.kolor} transition-colors`}
        style={{ height: proc(dane.mediana) }}
      />
      {dane.ofert >= 4 && (
        <span className="text-gray-700" aria-hidden="true">
          <span className="absolute left-1/2 -translate-x-1/2 w-0.5 bg-current" style={{ bottom: proc(dane.q1), height: `${((dane.q3 - dane.q1) / SKALA) * 100}%` }} />
          <span className="absolute left-1/2 -translate-x-1/2 w-2.5 h-0.5 bg-current" style={{ bottom: proc(dane.q1) }} />
          <span className="absolute left-1/2 -translate-x-1/2 w-2.5 h-0.5 bg-current" style={{ bottom: proc(dane.q3) }} />
        </span>
      )}
      <span
        className="absolute left-1/2 -translate-x-1/2 mb-1 flex flex-col items-center leading-none whitespace-nowrap"
        style={{ bottom: proc(Math.max(dane.mediana, dane.q3)) }}
      >
        {/* Jednostka w drugiej linii: „34,3 tys. zł” w jednej nie mieści się nad słupkiem na telefonie. */}
        <span className="text-[11px] font-semibold text-gray-700 tabular-nums">{tys(dane.mediana)}</span>
        <span className="mt-0.5 text-[9px] text-gray-500">tys. zł</span>
      </span>
    </button>
  )
}

const Dymek = ({ aktywny }) => {
  if (!aktywny) {
    return <p className="text-xs text-gray-400">Najedź na słupek albo go dotknij, żeby zobaczyć szczegóły.</p>
  }
  const g = GRUPY[aktywny.grupa]
  const seria = SERIE.find((s) => s.klucz === aktywny.seria)
  const d = g[aktywny.seria]
  return (
    <div className="text-xs text-gray-600 leading-5" aria-live="polite">
      <p className="font-semibold text-gray-800 flex items-center gap-1.5">
        <span className={`inline-block w-2.5 h-2.5 rounded-sm ${seria.kolor}`} aria-hidden="true" />
        {seria.nazwa}, {g.nazwa} <span className="font-normal text-gray-500">({d.opis})</span>
      </p>
      <p>
        Mediana <b className="text-gray-800">{zl(d.mediana)}</b>, połowa ofert od {zl(d.q1)} do {zl(d.q3)}.
        Najtaniej {zl(d.min)}, najdrożej {zl(d.max)}. {ofert(d.ofert)}, ok. {liczba(d.zlKwp)} zł za kWp.
      </p>
    </div>
  )
}

export const CenyZOfertWykres = () => {
  const [aktywny, setAktywny] = useState(null)
  return (
    <figure className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-6 shadow-sm">
      <p className="text-sm font-semibold text-gray-900">Ile kosztowała instalacja w prawdziwych ofertach</p>
      <p className="text-xs text-gray-500 mt-0.5">Cena środkowej oferty (mediana), brutto z montażem, przed dotacją. Jesień 2026</p>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-gray-600">
        {SERIE.map((s) => (
          <span key={s.klucz} className="inline-flex items-center gap-1.5">
            <span className={`inline-block w-3 h-3 rounded-sm ${s.kolor}`} aria-hidden="true" />{s.nazwa}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5">
          <span className="relative inline-block w-3 h-3 text-gray-700" aria-hidden="true">
            <span className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-0.5 bg-current" />
            <span className="absolute left-0 right-0 top-0 h-0.5 bg-current" />
            <span className="absolute left-0 right-0 bottom-0 h-0.5 bg-current" />
          </span>
          połowa ofert w tym przedziale
        </span>
      </div>

      <p className="mt-4 text-[10px] text-gray-400" aria-hidden="true">tys. zł</p>
      <div className="mt-1 flex" onMouseLeave={() => setAktywny(null)}>
        <div className="relative w-9 flex-none" style={{ height: 230 }} aria-hidden="true">
          {PODZIALKA.map((p) => (
            <span key={p} className="absolute right-1.5 translate-y-1/2 text-[10px] text-gray-400 tabular-nums" style={{ bottom: proc(p) }}>
              {p / 1000}
            </span>
          ))}
        </div>
        <div className="relative flex-1 min-w-0">
          <div className="relative" style={{ height: 230 }}>
            {PODZIALKA.map((p) => (
              <span key={p} className={`absolute left-0 right-0 h-px ${p === 0 ? 'bg-gray-300' : 'bg-gray-100'}`} style={{ bottom: proc(p) }} aria-hidden="true" />
            ))}
            <div className="absolute inset-0 flex">
              {GRUPY.map((g, i) => (
                <div key={g.nazwa} className="flex-1 flex items-end justify-center gap-0.5">
                  {SERIE.map((s) => (
                    <Slupek key={s.klucz} seria={s} dane={g[s.klucz]} grupa={g.nazwa}
                            aktywny={aktywny?.grupa === i && aktywny?.seria === s.klucz}
                            onAktywny={() => setAktywny({ grupa: i, seria: s.klucz })} />
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div className="flex mt-2" aria-hidden="true">
            {GRUPY.map((g) => (
              <div key={g.nazwa} className="flex-1 text-center min-w-0">
                <p className="text-[11px] sm:text-xs font-medium text-gray-700 leading-tight">{g.nazwa}</p>
                <p className="text-[10px] text-gray-400">{ofert((g.pv?.ofert || 0) + g.mag.ofert)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 min-h-[3.5rem] border-t border-gray-100 pt-3">
        <Dymek aktywny={aktywny} />
      </div>
      <figcaption className="mt-2 text-xs text-gray-400">
        Kreska pokazuje przedział, w którym mieściła się środkowa połowa ofert. Skąd dane: na końcu artykułu.
      </figcaption>
    </figure>
  )
}


// Kalkulator „Ile może kosztować moja instalacja” na tych samych ofertach; model w `lib/cenyZOfert.js`.
const ulamek = (n) => String(n).replace('.', ',')

/*
 * Rysunek do kalkulatora: dom w stylu ikon strony głównej (ciemny obrys, niebieskie moduły) i wieże magazynu.
 * Moduły wypełniają dach proporcjonalnie do mocy (cały dach przy 20 kWp), bateria w wieży 2,5 kWh, w wieży mieści się 6 baterii (15 kWh), więc powyżej 15 kWh
 * rośnie druga wieża. Liczby są poglądowe, cenę liczy model. Animacja: moduły i baterie pojawiają się po kolei
 * (opacity i przesunięcie), bez ruchu przy `prefers-reduced-motion`.
 */
const KWP_NA_MODUL = 0.5
const KWP_MAX = 20
const KWH_NA_BATERIE = 2.5
const BATERII_W_WIEZY = 6

// Połać dachu (trapez w widoku z przodu) i rzędy modułów od okapu do kalenicy.
const DACH = { okap: 126, kalenica: 50, lewyOkap: 26, prawyOkap: 254, lewaKalenica: 80, prawaKalenica: 200 }
const MODUL = { w: 15, h: 12, odstep: 2 }
const SLOTY = (() => {
  const sloty = []
  const xNa = (y, lewy) => {
    const u = (DACH.okap - y) / (DACH.okap - DACH.kalenica)
    return lewy ? DACH.lewyOkap + (DACH.lewaKalenica - DACH.lewyOkap) * u : DACH.prawyOkap + (DACH.prawaKalenica - DACH.prawyOkap) * u
  }
  for (let y = DACH.okap - 3 - MODUL.h; y >= DACH.kalenica + 2; y -= MODUL.h + MODUL.odstep) {
    const od = xNa(y, true) + 4
    const doX = xNa(y, false) - 4
    const ile = Math.floor((doX - od + MODUL.odstep) / (MODUL.w + MODUL.odstep))
    const start = (od + doX - ile * (MODUL.w + MODUL.odstep) + MODUL.odstep) / 2
    for (let i = 0; i < ile; i++) sloty.push({ x: start + i * (MODUL.w + MODUL.odstep), y })
  }
  return sloty
})()

const pojawianie = 'transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none'

const RysunekInstalacji = ({ kwp, kwh }) => {
  // Przy 20 kWp (koniec suwaka) moduły zajmują cały dach (właściciel 2026-10-29), więc na rysunku liczba modułów
  // to część dachu, a nie dosłownie moc / 0,5 kWp; prawdziwą liczbę modułów podaje podpis pod rysunkiem.
  const moduly = Math.round((Math.min(kwp, KWP_MAX) / KWP_MAX) * SLOTY.length)
  const baterie = kwh > 0 ? Math.ceil(kwh / KWH_NA_BATERIE) : 0
  const wieze = [0, 1].map((w) => Array.from({ length: BATERII_W_WIEZY }, (_, i) => w * BATERII_W_WIEZY + i))
  return (
    <svg viewBox="0 0 360 214" className="w-full h-auto text-gray-900" role="img"
         aria-label={`Dom z ${Math.round(kwp / KWP_NA_MODUL)} modułami na dachu${baterie ? ` i magazynem ${baterie} baterii` : ' bez magazynu'}`}>
      {/* ziemia */}
      <path d="M8 200h344" stroke="currentColor" strokeOpacity=".25" strokeWidth="2" strokeLinecap="round" />
      {/* bryła domu i połać */}
      <path d="M44 126v74h192v-74" fill="rgb(var(--vv-bg-white))" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
      <path d={`M${DACH.lewyOkap} ${DACH.okap} L${DACH.lewaKalenica} ${DACH.kalenica} H${DACH.prawaKalenica} L${DACH.prawyOkap} ${DACH.okap} Z`}
            fill="rgb(var(--vv-bg-gray-50))" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
      <path d="M118 200v-40h28v40" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <rect x="66" y="146" width="30" height="24" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
      <rect x="186" y="146" width="30" height="24" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
      {/* moduły */}
      {SLOTY.map((s, i) => {
        const jest = i < moduly
        return (
          <g key={i} className={pojawianie}
             style={{ opacity: jest ? 1 : 0, transform: `translateY(${jest ? 0 : -6}px)`, transitionDelay: jest ? `${(i % 8) * 18}ms` : '0ms' }}>
            <rect x={s.x} y={s.y} width={MODUL.w} height={MODUL.h} rx="1.2" fill="#2563EB" />
            <path d={`M${s.x + MODUL.w / 2} ${s.y + 1}v${MODUL.h - 2}M${s.x + 1} ${s.y + MODUL.h / 2}h${MODUL.w - 2}`} stroke="#BFDBFE" strokeWidth=".6" />
          </g>
        )
      })}
      {/* falownik na ścianie i przewód do magazynu */}
      <rect x="244" y="150" width="20" height="26" rx="3" fill="rgb(var(--vv-bg-white))" stroke="currentColor" strokeWidth="2" />
      <circle cx="254" cy="158" r="2" fill="#10B981" />
      <path d="M264 170h18" stroke="currentColor" strokeWidth="1.5" className={pojawianie} style={{ opacity: baterie ? 0.6 : 0 }} />
      {/* wieże magazynu: baterie od dołu */}
      {wieze.map((wieza, w) => (
        <g key={w}>
          {wieza.map((n, poziom) => {
            const jest = n < baterie
            const x = 284 + w * 36
            const y = 200 - 3 - (poziom + 1) * 16
            return (
              <g key={n} className={pojawianie}
                 style={{ opacity: jest ? 1 : 0, transform: `translateY(${jest ? 0 : -14}px)`, transitionDelay: jest ? `${poziom * 45}ms` : '0ms' }}>
                <rect x={x} y={y} width="30" height="14" rx="2.5" fill="rgb(var(--vv-bg-white))" stroke="currentColor" strokeWidth="2" />
                <rect x={x + 5} y={y + 5} width="10" height="4" rx="1" fill="#2563EB" />
                <circle cx={x + 23} cy={y + 7} r="1.6" fill="#10B981" />
              </g>
            )
          })}
        </g>
      ))}
    </svg>
  )
}

/** Suwak platformy (`vv-range` z index.css, jak budżet na stronie głównej): dymek z wartością nad uchwytem. */
const Suwak = ({ id, etykieta, wartosc, min, max, krok, onZmiana, dymek, skrajne }) => {
  const procent = ((wartosc - min) / (max - min)) * 100
  return (
    <div>
      <label htmlFor={id} className="text-sm font-semibold text-gray-800">{etykieta}</label>
      <div className="relative pt-9 mt-0.5">
        {/* Bez animacji `left`, jak przy suwaku budżetu: przejście calc() z procentami potrafi zatrzymać dymek. */}
        <span className="absolute top-0 -translate-x-1/2 px-2.5 py-1 rounded-lg bg-blue-600 text-white text-[13px] font-bold whitespace-nowrap shadow-md"
              style={{ left: `calc(${procent}% + ${13 - 0.26 * procent}px)` }} aria-hidden="true">
          {dymek}
          <span className="absolute left-1/2 -translate-x-1/2 top-full w-2 h-2 -mt-1 rotate-45 bg-blue-600" />
        </span>
        <input id={id} type="range" min={min} max={max} step={krok} value={wartosc}
               onChange={(e) => onZmiana(Number(e.target.value))} aria-valuetext={dymek}
               className="vv-range"
               style={{
                 background: `linear-gradient(to right, #1d4ed8 0%, #3b82f6 ${procent}%,
                   rgb(var(--vv-bg-gray-200)) ${procent}%, rgb(var(--vv-bg-gray-200)) 100%)`,
               }} />
      </div>
      <div className="flex items-center justify-between mt-1.5 text-[11px] text-gray-400" aria-hidden="true">
        <span>{skrajne[0]}</span><span>{skrajne[1]}</span>
      </div>
    </div>
  )
}

export const CenaKalkulator = () => {
  const [kwp, setKwp] = useState(10)
  const [kwh, setKwh] = useState(20)
  const [premium, setPremium] = useState(false)
  const zPremium = premium && kwh > 0
  const c = policzCene(kwp, kwh, zPremium)
  const minPme2 = Math.max(10, Math.ceil(2 * kwp))
  const moduly = Math.round(kwp / KWP_NA_MODUL)
  return (
    <section className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden" aria-labelledby="kalk-ceny-tytul">
      {/* Jedna kolumna: kolumna artykułu bywa wąska (obok spis treści), a rysunek ma być duży. */}
      <div className="px-4 pt-5 sm:px-6">
        <p id="kalk-ceny-tytul" className="text-base font-bold text-gray-900">Ile może kosztować Twoja instalacja</p>
        <p className="text-xs text-gray-500 mt-0.5">Na tych samych ofertach co wykres. Brutto z montażem, przed dotacją.</p>
      </div>
      <div className="mt-4 mx-4 sm:mx-6 rounded-xl bg-gray-50 px-3 pt-4 pb-2">
        <div className="max-w-[460px] mx-auto"><RysunekInstalacji kwp={kwp} kwh={kwh} /></div>
        <p className="mt-1 text-xs text-gray-500 text-center tabular-nums">
          {moduly} modułów po ok. 500 W{kwh > 0 ? `, magazyn ${ulamek(kwh)} kWh` : ', bez magazynu'}
        </p>
      </div>

      <div className="px-4 pb-5 sm:px-6 sm:pb-6">
          <div className="mt-5 grid gap-5 sm:grid-cols-2 sm:gap-8">
            <Suwak id="kalk-kwp" etykieta="Moc instalacji" wartosc={kwp} min={3} max={KWP_MAX} krok={0.5} onZmiana={setKwp}
                   dymek={`${ulamek(kwp)} kWp`} skrajne={['3 kWp', '20 kWp']} />
            <Suwak id="kalk-kwh" etykieta="Magazyn energii" wartosc={kwh} min={0} max={30} krok={1} onZmiana={setKwh}
                   dymek={kwh === 0 ? 'bez magazynu' : `${kwh} kWh`} skrajne={['bez', '30 kWh']} />
          </div>

          <div className="mt-5 inline-flex rounded-xl bg-gray-100 p-1 text-sm" role="group" aria-label="Rodzaj sprzętu">
            {[[false, 'Typowy sprzęt'], [true, 'Premium']].map(([wartosc, nazwa]) => (
              <button key={nazwa} type="button" disabled={kwh === 0 && wartosc} aria-pressed={zPremium === wartosc}
                      onClick={() => setPremium(wartosc)}
                      className={`px-3.5 py-1.5 rounded-lg font-semibold transition-colors disabled:cursor-not-allowed disabled:text-gray-400 ${
                        zPremium === wartosc ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
                {nazwa}
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-[11px] text-gray-400">
            {kwh === 0 ? 'Premium liczymy tylko z magazynem: bez magazynu było za mało takich ofert.' : 'Premium to np. Sigenergy, Fronius, Huawei.'}
          </p>

          <div className="mt-5 pt-4 border-t border-gray-100" aria-live="polite">
            <p className="text-xs text-gray-500">Połowa podobnych ofert kosztowała</p>
            <p className="mt-0.5 text-[28px] sm:text-[32px] font-extrabold text-blue-700 tabular-nums leading-tight">
              {liczba(c.od)}<span className="text-gray-400 font-bold"> do </span>{liczba(c.do)} zł
            </p>
            <p className="mt-1 text-sm text-gray-600">
              Środkowa oferta ok. <b className="text-gray-900">{liczba(c.srodek)} zł</b>, ok. {liczba(c.srodek / kwp)} zł za kWp.
            </p>
            <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
              <span className={`px-2 py-0.5 rounded-full ${c.podobne >= 8 ? 'bg-gray-100 text-gray-600' : 'bg-amber-50 text-amber-800'}`}>
                {c.podobne >= 8 ? `${c.podobne} podobnych ofert` : c.podobne > 0 ? `tylko ${c.podobne} podobnych ofert, wynik orientacyjny` : 'brak podobnych ofert, przybliżenie'}
              </span>
              {kwh > 0 && (
                <span className={`px-2 py-0.5 rounded-full ${kwh >= minPme2 ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                  {kwh >= minPme2 ? 'pojemność spełnia warunek PME2' : `do PME2 potrzeba min. ${minPme2} kWh`}
                </span>
              )}
            </div>
          </div>
      </div>
    </section>
  )
}
